// 认证相关工具函数
import { ElMessage } from 'element-plus'
import { defHttp } from './axios.js'
import { getApiUrl } from '../config.js'
import { 
  isDevModeEnabled, 
  devModeCheckCertification,
  isDevModeLoggedIn 
} from './devMode.js'

/**
 * 处理token过期的情况
 * @param {Object} router - Vue Router实例
 * @param {string} message - 提示消息，默认为'登录已过期，请重新登录'
 */
export function handleTokenExpired(router, message = '登录已过期，请重新登录') {
  // 显示提示消息
  ElMessage.error(message)
  
  // 清除本地存储的认证信息
  localStorage.removeItem('auth_token')
  localStorage.removeItem('user_id')
  clearUserProfileCache()
  
  // 跳转到登录页面
  if (router && router.currentRoute.value.path !== '/login' && !router.currentRoute.value.path.startsWith('/login/')) {
    router.push('/login')
  } else if (!router) {
    // 无路由实例的场景（例如在请求拦截器中调用），使用window.location强制跳转
    // 检查当前路径，避免重复跳转
    const currentPath = window.location.pathname
    if (currentPath !== '/login' && !currentPath.startsWith('/login/')) {
      window.location.href = '/login'
    }
  }
}

/**
 * 检查token是否有效
 * @returns {boolean} token是否有效
 */
export function isTokenValid() {
  try {
    const token = localStorage.getItem('auth_token')
    if (!token) return false
    
    // 基本检查：token不为空且长度合理
    if (token.length < 5) {
      try {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_id')
      } catch (e) {
        console.error('清除无效token失败:', e)
      }
      return false
    }
    
    // 暂时只做基本检查，避免影响正常访问
    // 实际的token有效性验证应该通过API调用来检查
    return true
  } catch (e) {
    console.error('检查token有效性失败:', e)
    return false
  }
}

/**
 * 检查用户是否已登录
 * @returns {boolean} 用户是否已登录
 */
export function isLoggedIn() {
  try {
    // 优先使用真实 token 判断（即使在开发模式下，只要有真实 token 就认为已登录）
    const hasToken = !!localStorage.getItem('auth_token')
    if (hasToken) {
      return isTokenValid()
    }
    // 无真实 token 时，才回退到开发模式登录判断
    if (isDevModeEnabled()) {
      return isDevModeLoggedIn()
    }
    return false
  } catch (e) {
    console.error('检查登录状态失败:', e)
    return false
  }
}

/**
 * 检查用户认证状态
 * @returns {Promise<Object>} 返回认证状态信息
 */
export async function checkCertificationStatus() {
  // 如存在真实 token，则优先使用后端真实数据（即使在开发模式）
  const existingToken = localStorage.getItem('auth_token')
  if (!existingToken) {
    // 仅当无真实 token 且开发模式启用时，才返回模拟数据
    if (isDevModeEnabled()) {
      return await devModeCheckCertification()
    }
  }
  
  try {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      return { isLoggedIn: false, certificationState: 0, needLogin: true }
    }

    // 检查token是否有效
    if (!isTokenValid()) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_id')
      return { isLoggedIn: false, certificationState: 0, needLogin: true }
    }

    // 调用用户信息接口获取认证状态
    const res = await defHttp.get('/app/user/info')
    const data = res?.data?.data || res?.data || {}
    
    return {
      isLoggedIn: true,
      certificationState: data.certificationState || 0,
      userInfo: data,
      needLogin: false
    }
    } catch (error) {
      console.error('检查认证状态失败:', error)
      // 如果是401错误，说明token无效，需要重新登录
    if (error?.response?.status === 401) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_id')
      return { isLoggedIn: false, certificationState: 0, needLogin: true }
    }
    // 如果是403错误，说明用户被冻结，不触发认证检查，直接返回
    if (error?.response?.status === 403 || error?.response?.data?.code === 403) {
      // 403错误已经在axios拦截器中处理，这里直接返回，不触发后续认证检查
      return { isLoggedIn: true, certificationState: 0, needLogin: false, isFrozen: true }
    }
    return { isLoggedIn: false, certificationState: 0, needLogin: false }
  }
}

/**
 * 处理未认证的情况
 * @param {Object} router - Vue Router实例
 * @param {string} message - 提示消息
 * @param {boolean} needLogin - 是否需要登录
 */
export async function handleUncertified(router, message = null, needLogin = false) {
  // 如果没有提供消息，使用默认配置
  if (!message) {
    try {
      const { CERTIFICATION_MESSAGES } = await import('@/config')
      message = CERTIFICATION_MESSAGES.NOT_CERTIFIED
    } catch {
      message = '您还未完成实名认证，请立即认证'
    }
  }
  if (needLogin) {
    // 需要登录，跳转到登录页面
    if (router) {
      router.push('/login')
    } else {
      window.location.href = '/login'
    }
  } else {
    // 显示认证提示弹窗
    showCertificationDialog(router, message)
  }
}

/**
 * 显示认证提示弹窗
 * @param {Object} router - Vue Router实例
 * @param {string} message - 提示消息
 */
export function showCertificationDialog(router, message) {
  // 创建全局认证弹窗实例
  const showDialog = () => {
    // 动态导入Element Plus组件
    import('element-plus').then(({ ElMessageBox }) => {
      ElMessageBox.confirm(
        message,
        '认证提示',
        {
          confirmButtonText: '立即认证',
          cancelButtonText: '稍后再说',
          type: 'warning',
          showCancelButton: true,
          closeOnClickModal: false,
          closeOnPressEscape: false
        }
      ).then(() => {
        // 用户点击立即认证，跳转到认证页面
        if (router) {
          router.push('/cert/idCard')
        } else {
          // 如果没有router实例，使用window.location
          window.location.href = '/cert/idCard'
        }
      }).catch(() => {
        // 用户点击稍后再说，不做任何操作
      })
    }).catch(error => {
      console.error('显示认证弹窗失败:', error)
      // 降级处理：使用alert
      if (confirm(message + '\n\n点击确定前往认证页面')) {
        if (router) {
          router.push('/cert/idCard')
        } else {
          window.location.href = '/cert/idCard'
        }
      }
    })
  }

  // 延迟执行，避免在请求拦截器中立即弹出
  setTimeout(showDialog, 100)
}

/**
 * 清除认证信息
 */
export function clearAuth() {
  try {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_id')
    clearUserProfileCache()
  } catch (e) {
    console.error('清除认证信息失败:', e)
  }
}

const USER_PROFILE_CACHE_KEY = 'user_profile_cache'

/**
 * 读取「我的」页用户资料缓存（sessionStorage，用于避免返回页面时闪未登录态）
 */
export function getUserProfileCache() {
  try {
    const raw = sessionStorage.getItem(USER_PROFILE_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.userInfo?.id) return null
    return parsed
  } catch (e) {
    return null
  }
}

/**
 * 写入「我的」页用户资料缓存
 */
export function setUserProfileCache(payload) {
  try {
    if (!payload?.userInfo?.id) return
    sessionStorage.setItem(USER_PROFILE_CACHE_KEY, JSON.stringify(payload))
  } catch (e) {
    console.warn('写入用户资料缓存失败:', e)
  }
}

/**
 * 清除「我的」页用户资料缓存
 */
export function clearUserProfileCache() {
  try {
    sessionStorage.removeItem(USER_PROFILE_CACHE_KEY)
  } catch (e) {
    console.warn('清除用户资料缓存失败:', e)
  }
}

/**
 * 从JWT token解析用户ID（若token为JWT）
 * 尝试读取 payload 中的 id/userId/uid/sub
 */
export function extractUserIdFromToken(token) {
  if (!token || typeof token !== 'string') return ''
  const parts = token.split('.')
  if (parts.length !== 3) return ''
  try {
    const payloadBase64 = parts[1]
    const json = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(decodeURIComponent(escape(json)))
    const userId =  payload.uid  || ''
    return userId ? String(userId) : ''
  } catch (e) {
    return ''
  }
}

// 解析 JWT 过期时间（单位：毫秒时间戳），若不是标准 JWT 或无 exp 字段，则返回 0
export function getTokenExpirationMs(token = localStorage.getItem('auth_token')) {
  if (!token || typeof token !== 'string') return 0
  const parts = token.split('.')
  if (parts.length !== 3) return 0
  try {
    const payloadBase64 = parts[1]
    const json = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(decodeURIComponent(escape(json)))
    const expSeconds = Number(payload && payload.exp)
    if (!expSeconds || Number.isNaN(expSeconds)) return 0
    return expSeconds * 1000
  } catch (_) {
    return 0
  }
}

// 距离 token 过期剩余毫秒数；未知时返回 Infinity，已过期返回 <= 0
export function getTokenRemainingMs(token = localStorage.getItem('auth_token')) {
  const expMs = getTokenExpirationMs(token)
  if (!expMs) return Number.POSITIVE_INFINITY
  return expMs - Date.now()
}

// 活跃度跟踪（仅内存级别，避免频繁写入存储）
let lastActiveAtMs = Date.now()
let activityInited = false
const defaultActiveWindowMs = 2 * 60 * 1000 // 2 分钟内视为活跃

function updateLastActive() {
  lastActiveAtMs = Date.now()
}

export function initUserActivityTracker() {
  if (activityInited || typeof window === 'undefined') return
  activityInited = true
  const events = ['mousemove', 'keydown', 'click', 'touchstart', 'visibilitychange']
  events.forEach(evt => window.addEventListener(evt, updateLastActive, { passive: true }))
}

export function isUserActive(activeWindowMs = defaultActiveWindowMs) {
  return Date.now() - lastActiveAtMs <= activeWindowMs
}

// 判断是否需要刷新：用户活跃且剩余时间小于阈值且未过期
export function shouldRefreshToken(thresholdMs = 2 * 60 * 1000) {
  const token = localStorage.getItem('auth_token')
  if (!token) return false
  
  // 检查 token 是否为 JWT 格式（包含两个点）
  const isJWT = token.split('.').length === 3
  if (!isJWT) {
    // 非 JWT token，不支持自动刷新
    console.log('当前 token 不是 JWT 格式，跳过自动刷新')
    return false
  }
  
  const remaining = getTokenRemainingMs(token)
  if (remaining === Number.POSITIVE_INFINITY) return false
  if (remaining <= 0) return false
  return isUserActive() && remaining <= thresholdMs
}

// 使用独立 axios 实例执行刷新，避免与全局拦截器相互影响
export async function refreshTokenRequest() {
  const token = localStorage.getItem('auth_token')
  if (!token) throw new Error('NO_TOKEN')
  const baseURL = (typeof window !== 'undefined') ? getApiUrl() : ''
  const { default: axios } = await import('axios')
  // 仅使用标准的 Authorization 头，避免自定义 token 头导致 CORS 预检失败
  const inst = axios.create({ baseURL, timeout: 15000, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } })

  // 后端映射为 POST /app/auth/refresh
  const res = await inst.post('/app/auth/refresh')
  
  // 检查后端是否返回错误
  const data = res?.data
  if (data && (data.code === 500 || data.code >= 400)) {
    const errorMsg = data.msg || '刷新令牌失败'
    throw new Error(errorMsg)
  }
  
  // 尝试多种可能的数据结构
  let newToken = null
  
  // 情况1: { data: { token: "xxx" } }
  if (data?.data) {
    newToken = data.data.token || data.data.accessToken || data.data.authToken
  }
  
  // 情况2: { token: "xxx" }
  if (!newToken && data) {
    newToken = data.token || data.accessToken || data.authToken
  }
  
  // 情况3: { code: 200, data: "token_string" }
  if (!newToken && data?.data && typeof data.data === 'string') {
    newToken = data.data
  }
  
  // 情况4: 直接返回token字符串
  if (!newToken && typeof data === 'string') {
    newToken = data
  }
  
  if (!newToken || typeof newToken !== 'string') {
    throw new Error('REFRESH_RETURN_INVALID')
  }
  
  localStorage.setItem('auth_token', newToken)
  const uid = extractUserIdFromToken(newToken)
  if (uid) {
    localStorage.setItem('user_id', uid)
  }
  return newToken
}
